import { useEffect } from 'react';
import './FieryWrathAnimation.css';

/**
 * PROFESSIONAL FIERY WRATH ANIMATION
 * Dark-type special move - transforms rage into dark fire wave
 * 
 * Key Features:
 * - Dark-type special attack (90 base power, 100% accuracy)
 * - 20% chance to flinch
 * - Galarian Moltres signature move with dark flame aura
 * - Professional gaming quality with sinister fire effects
 */
export default function FieryWrathAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Dark Rage Buildup (0-600ms)
    playerEl.classList.add('fiery-wrath-user-charge');

    const darkAura = document.createElement('div');
    darkAura.className = 'fiery-wrath-dark-aura';
    darkAura.style.left = `${playerX - 80}px`;
    darkAura.style.top = `${playerY - 80}px`;
    container.appendChild(darkAura);
    elements.push(darkAura);

    // Dark flames rising around player
    for (let i = 0; i < 12; i++) {
      const flame = document.createElement('div');
      flame.className = 'fiery-wrath-dark-flame';
      flame.style.left = `${playerX + (Math.random() - 0.5) * 80 - 6}px`;
      flame.style.top = `${playerY + (Math.random() - 0.5) * 40 - 6}px`;
      flame.style.animationDelay = `${i * 40}ms`;
      container.appendChild(flame);
      elements.push(flame);
    }

    // PHASE 2: Dark Fire Wave (600-1100ms)
    const phase2Timer = setTimeout(() => {
      // Dark fire projectile wave
      const wave = document.createElement('div');
      wave.className = 'fiery-wrath-wave';
      wave.style.left = `${playerX}px`;
      wave.style.top = `${playerY}px`;
      wave.style.setProperty('--dx', `${dx}px`);
      wave.style.setProperty('--dy', `${dy}px`);
      container.appendChild(wave);
      elements.push(wave);

      // Dark ember trail
      for (let i = 0; i < 16; i++) {
        const emberTimer = setTimeout(() => {
          const ember = document.createElement('div');
          ember.className = 'fiery-wrath-ember';
          const progress = i / 16;
          ember.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 25 - 5}px`;
          ember.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 25 - 5}px`;
          container.appendChild(ember);
          elements.push(ember);
        }, i * 28);
        elements.push({ timer: emberTimer, type: 'timer' });
      }
    }, 600);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Wrathful Impact (1100-1600ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('fiery-wrath-opponent-hit');

      // Dark fire explosion
      const explosion = document.createElement('div');
      explosion.className = 'fiery-wrath-explosion';
      explosion.style.left = `${opponentX - 110}px`;
      explosion.style.top = `${opponentY - 110}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Dark burst particles
      for (let i = 0; i < 20; i++) {
        const burstAngle = (i / 20) * 360;
        const burstDist = 55 + Math.random() * 50;
        const burst = document.createElement('div');
        burst.className = 'fiery-wrath-burst';
        burst.style.left = `${opponentX - 5}px`;
        burst.style.top = `${opponentY - 5}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 12}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }

      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'fiery-wrath-shockwave';
          sw.style.left = `${opponentX - 70}px`;
          sw.style.top = `${opponentY - 70}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }

      // Lingering dark flames at impact
      for (let i = 0; i < 6; i++) {
        const linger = document.createElement('div');
        linger.className = 'fiery-wrath-linger';
        linger.style.left = `${opponentX + (Math.random() - 0.5) * 80 - 8}px`;
        linger.style.top = `${opponentY + (Math.random() - 0.5) * 50 - 8}px`;
        linger.style.animationDelay = `${i * 40}ms`;
        container.appendChild(linger);
        elements.push(linger);
      }
    }, 1100);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // CLEANUP (1800ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('fiery-wrath-user-charge');
      opponentEl.classList.remove('fiery-wrath-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1800);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('fiery-wrath-user-charge');
      opponentEl.classList.remove('fiery-wrath-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

FieryWrathAnimation.moveType = 'dark';
